import { useState } from 'react';
import { EditPencil, MoreHoriz, Plus, Trash } from 'iconoir-react';
import { useTranslation } from 'react-i18next';

import ConfirmationModal from 'components/blocks/confirmation-modal/confirmation-modal';
import { Button } from 'components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  dropdownMenuItemDestructiveClass,
} from 'components/ui/dropdown-menu';
import { useToast } from 'hooks/use-toast';
import { useIsOwner } from 'hooks/use-is-owner';
import { useDeletePromptFolder } from '../hooks/use-prompts';
import type { PromptFolder } from '../types';
import { EditFolderModal } from './edit-folder-modal';

type FolderActionDropdownProps = {
  folder: PromptFolder;
  onAddPrompt: () => void;
};

export function FolderActionDropdown({ folder, onAddPrompt }: FolderActionDropdownProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const canEdit = useIsOwner(folder);
  const { mutateAsync: deleteFolder, isPending } = useDeletePromptFolder();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const handleDelete = async () => {
    if (!canEdit) return;

    try {
      await deleteFolder(folder.id);
      toast({
        variant: 'success',
        title: t('TOAST_FOLDER_DELETED_TITLE') || 'Folder deleted',
        description:
          t('TOAST_FOLDER_DELETED_DESCRIPTION', { name: folder.name }) ||
          `${folder.name} was deleted successfully.`,
      });
      setIsDeleteModalOpen(false);
    } catch (error) {
      toast({
        title: t('TOAST_FAILED_TO_DELETE_FOLDER_TITLE') || 'Failed to delete folder',
        description:
          error instanceof Error
            ? error.message
            : t('TOAST_PLEASE_TRY_AGAIN_DESCRIPTION') || 'Please try again.',
        variant: 'destructive',
      });
    }
  };

  const showAddPrompt = canEdit;
  const showEditFolder = !folder.isDefault;
  const showDeleteFolder = canEdit && !folder.isDefault;

  if (!showAddPrompt && !showEditFolder && !showDeleteFolder) {
    return null;
  }

  return (
    <>
      <DropdownMenu modal={false} open={isMenuOpen} onOpenChange={setIsMenuOpen}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 rounded text-grey-700 opacity-100 hover:bg-grey-50 hover:text-grey-700 focus-visible:ring-0 focus-visible:ring-offset-0 dark:text-white/70 dark:hover:bg-white/10"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
            }}
          >
            <MoreHoriz width={20} height={20} strokeWidth={1.75} />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" side="right" sideOffset={6} className="min-w-[191px]">
          {showAddPrompt ? (
            <DropdownMenuItem
              onSelect={(event) => {
                event.preventDefault();
                event.stopPropagation();
                setIsMenuOpen(false);
                onAddPrompt();
              }}
            >
              <Plus width={20} height={20} strokeWidth={1.5} />
              {t('ADD_PROMPT') || 'Add prompt'}
            </DropdownMenuItem>
          ) : null}

          {showEditFolder ? (
            <DropdownMenuItem
              onSelect={(event) => {
                event.preventDefault();
                event.stopPropagation();
                setIsMenuOpen(false);
                setIsEditModalOpen(true);
              }}
            >
              <EditPencil width={20} height={20} strokeWidth={1.5} />
              {t('EDIT_FOLDER') || 'Edit folder'}
            </DropdownMenuItem>
          ) : null}

          {showDeleteFolder ? (
            <DropdownMenuItem
              className={dropdownMenuItemDestructiveClass}
              onSelect={(event) => {
                event.preventDefault();
                event.stopPropagation();
                setIsMenuOpen(false);
                setIsDeleteModalOpen(true);
              }}
            >
              <Trash width={20} height={20} strokeWidth={1.5} />
              {t('DELETE_FOLDER') || 'Delete folder'}
            </DropdownMenuItem>
          ) : null}
        </DropdownMenuContent>
      </DropdownMenu>

      {!folder.isDefault && (
        <EditFolderModal folder={folder} open={isEditModalOpen} onOpenChange={setIsEditModalOpen} />
      )}

      {showDeleteFolder ? (
        <ConfirmationModal
          open={isDeleteModalOpen}
          onOpenChange={setIsDeleteModalOpen}
          title={t('DELETE_FOLDER_CONFIRMATION_TITLE') || 'Delete folder'}
          description={
            t('DELETE_FOLDER_CONFIRMATION_DESCRIPTION', { name: folder.name }) ||
            `Are you sure you want to delete ${folder.name}? All prompts in this folder will also be deleted.`
          }
          onConfirm={handleDelete}
          confirmText={t('DELETE') || 'Delete'}
          cancelText={t('CANCEL') || 'Cancel'}
          preventAutoClose
          isLoading={isPending}
          confirmVariant="delete"
        />
      ) : null}
    </>
  );
}
